import { InputAdornment, Typography } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme, makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/SearchOutlined'
import { Box } from '@mui/material'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'

import { DebouncedTextField } from 'src/components/Input/DebouncedTextField'
import { useOnboardingJoyride } from 'src/Joyride/OnboardingJoyride/OnBoardingJoyrideWrapper'

import Card, { CardMenuContainer } from '../../Card'
import { IColumnProps } from '../VirtualizedTable'

import ExportableTable from './ExportableTable'

export interface ISearchBar {
  onChange: any
  placeholder: string
  value?: string
}

interface IFullExportableTable {
  className?: string
  columns: IColumnProps[]
  data: any[]
  dataTestId?: string
  displayLogo?: boolean
  filter?: { icon: any; onClick: (event: React.MouseEvent<HTMLElement>) => void; title: string }
  hideFilterBtn?: boolean
  isLoading?: boolean
  noDataMessage?: string
  onClickExport?: (event: React.MouseEvent<HTMLElement>) => any
  searchBar?: ISearchBar
  totalCount?: number
  truncated?: boolean
}

export function FullExportableTable(props: IFullExportableTable) {
  const { displayLogo = true, isLoading = false } = props
  const classes = useStyles()

  const [isOverDimensions, setIsOverDimensions] = useState(false)

  const { resumeJoyride } = useOnboardingJoyride()

  useEffect(() => {
    if (!isLoading) {
      resumeJoyride()
    }
  }, [isLoading])

  return (
    <Card className={classes.card} displayLogo={displayLogo}>
      <CardMenuContainer
        onClickExport={props.onClickExport}
        filter={props.filter}
        displayTooltip={isOverDimensions}
        hideFilterBtn={props.hideFilterBtn}
        rowCount={props.data.length}
      >
        {props.searchBar && (
          <div className={classes.searchContainer}>
            <DebouncedTextField
              id="outlined-adornment"
              variant="outlined"
              type="text"
              value={props.searchBar.value}
              onFinalChange={props.searchBar.onChange}
              timeToWait={0}
              placeholder={props.searchBar.placeholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                classes: {
                  root: classes.input,
                  notchedOutline: classes.searchInputContainer,
                  inputAdornedStart: classes.searchInput,
                },
              }}
            />
          </div>
        )}
      </CardMenuContainer>
      {!isLoading && (
        <Box display="grid" flex={1} sx={{ overflowY: 'scroll' }}>
          {props.data.length ? (
            <ExportableTable
              data={props.data}
              className={props.className}
              columns={props.columns}
              totalCount={props.totalCount}
              truncated={props.truncated}
              setIsOverDimension={setIsOverDimensions}
              dataTestId={props.dataTestId}
            />
          ) : (
            <div className={classNames(classes.table, classes.centered)}>
              <Typography className={classes.noDataText}>{props.noDataMessage}</Typography>
            </div>
          )}
        </Box>
      )}
      {isLoading && (
        <div className={classNames(classes.table, classes.centered)}>
          <CircularProgress disableShrink={true} />
        </div>
      )}
    </Card>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    minWidth: '50%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'inherit',
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    marginTop: theme.spacing(14),
  },
  input: {
    backgroundColor: '#ECF5FF',
    borderRadius: '4px',
    height: '36px',
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '17px',
  },
  noDataText: {
    fontSize: 20,
    color: 'rgba(152, 152, 152, 0.87)',
  },
  searchContainer: {
    margin: '0px 5px',
  },
  searchInput: {
    padding: '10px 0px',
    maxWidth: '300px',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '17px',
  },
  searchInputContainer: {
    border: 'none',
  },
  table: {
    display: 'flex',
    minHeight: '300px',
  },
}))
